<template>
    <div>
        <!-- Restart Account -->
        <restart-account v-if="canRestartAccount"></restart-account>

        <div v-else class="main" :class="{ 'main-container': newDashboard }">
            <!-- Loader -->
            <div v-if="!subscriptionsHaveFetched" class="text-center">
                <loader/>
                <p>{{$t("DASHBOARD_LOADER")}}</p>
            </div>

            <!-- Old Dashboard -->
            <div v-if="subscriptionsHaveFetched && !newDashboard">
                <!-- Dashboard User Info -->
                <div v-if="hasUserFetched" class="text-center container mx-auto px-2 md:px-16 lg:px-32 xl:px-64 pb-2">
                    <div class="px-4">
                        <p>{{ $t('DASHBOARD_HI') }} <span v-text="introductionText"></span></p>
                        <div v-if="hasPausedSubscription && !hasAnActiveSubscription">
                            <unpause-button :subscription="subscription" :allowRedirect="true"/>
                        </div>
                        <div class="mt-6 pb-3" v-if="isFlowerBondHolder">
                            <p v-html="$t('DASHBOARD_FLOWER_BOND_HOLDER')"></p>
                            <img :src="flowerBondHolderBanner" alt="Flower Bond Holder">
                        </div>
                        <div class="mt-6 pb-3" v-if="showFunFact">
                            <p>{{ $t('DASHBOARD_DID_YOU_KNOW') }}</p>
                            <p class="mt-0 pt-0">
                                {{ randomFact }}
                            </p>
                        </div>
                    </div>
                </div>

                <!-- Dashboard Banners -->
                <info-banner class="info-banner" position="dashboard"/>
                <div v-if="showBanner" class="message-banner">
                    <p class="message-banner--content" v-text="$t('DASHBOARD_BANNER')"></p>
                </div>

                <!-- Errors -->
                <div v-if="isError" class="error text-center">
                    <p class="message-banner--content" v-text="$t('DASHBOARD_ERROR_MESSAGE')"></p>
                </div>

                <div class="divider--container">
                    <div class="divider-white--top"></div>
                </div>

                <!-- Old Dashboard Tiles -->
                <div class="background--paper">
                    <div class="container text-center mx-auto px-2 md:px-16 lg:px-32 xl:px-64 py-6">
                        <div class="flex flex-wrap justify-center">
                            <div v-for="item in oldDashboardMenuItems1" :key="item.label" class="w-1/2 md:w-1/3 mb-2 md:mb-3 lg:mb-6 tiles-section-1">
                                <icon-box :icon="item" :hasGiftingCoupon="hasGiftingCoupon" :isLoaded="enableTiles"/>
                            </div>
                            <promotional-banners :customStyle="customStyle" :bannerData="bannerData" :isFreeVase="isFreeVase"/>
                            <div v-for="item in oldDashboardMenuItems2" :key="item.label" class="w-1/2 md:w-1/3 mb-2 md:mb-3 lg:mb-6">
                                <icon-box :icon="item" :hasGiftingCoupon="hasGiftingCoupon" :isLoaded="enableTiles"/>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="divider-paper--bottom"></div>
            </div>

            <!-- new dashboard -->
            <div v-if="newDashboard && subscriptionsHaveFetched">
                <subscription-state
                    :tiles="menuItems1"
                    :hasGiftingCoupon="hasGiftingCoupon"
                    :enableTiles="enableTiles"
                    :nearestUpcomingDate="nextPossibleDate"/>

                <div class="dashboard-container">
                    <promotional-banners :newDashboard="newDashboard" :bannerData="bannerData" :isFreeVase="isFreeVase"/>

                    <div class="flex flex-wrap justify-center">
                        <div v-for="(item, index) in menuItems2" :key="index" class="w-1/2 md:w-1/4 mb-2">
                            <icon-box :icon="item" :hasGiftingCoupon="hasGiftingCoupon" :isLoaded="enableTiles"/>
                        </div>
                    </div>

                    <div class="block text-center recommendation-section">
                        <recommendations
                            :className="'calendar'"
                            headerText="Add to your next delivery"
                            :numItems="4">
                        </recommendations>

                        <button
                            class="product__info__button product__info__button__green"
                            style="background-color: #262323"
                            v-text="$t('RECOMMENDATIONS_CALENDAR_SHOP_BUTTON')"
                            @click="goToShop">
                        </button>
                    </div>
                </div>
            </div>

        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import InfoBanner from '@/components/partials/account/InfoBanner.vue';
import NavigationLinks from '@/config/NavigationLinks';
import { IconBox } from '@/components/partials/account/dashboard';
import RestartAccount from '@/components/forms/account/RestartAccount';
import FactPool from '@/content/dashboard/facts';
import UnpauseButton from '../../components/partials/account/my-delivery-addresses/UnpauseButton.vue';
import { SUBSCRIPTION_STATUS_PAUSED } from '@/constants/Subscription';
import Loader from '@/components/structure/Loader.vue';
import PromotionalBanners from '@/components/partials/account/PromotionalBanners.vue';
import Recommendations from '@/components/partials/account/shop/Recommendations.vue';
import SubscriptionState from '@/components/partials/account/SubscriptionState.vue';

export default {
    name: 'Dashboard',
    data() {
        return {
            flowerBondHolderBanner: require('@/assets/images/banners/banner-bondholder.svg'),
        };
    },
    components: {
        IconBox,
        RestartAccount,
        UnpauseButton,
        InfoBanner,
        Loader,
        PromotionalBanners,
        Recommendations,
        SubscriptionState,
    },
    mounted() {
        this.$store.dispatch('account/fetchThemes');
        this.$store.dispatch('specialDates/getAllSpecialDates');
        this.$store.dispatch('account/fetchUserData', true)
            .then(() => this.$store.dispatch('gifting/fetchGiftingCoupons'))
            .then(() => this.$store.dispatch('account/fetchPromotionalBanners'));
        this.$store.commit('specialDates/updatedSelectedOccassion', null);

        zE(function() {
            zE.setHelpCenterSuggestions({ search: 'calendar' });
        });
    },
    computed: {
        ...mapGetters({
            firstName: 'account/getFirstName',
            isFlowerBondHolder: 'account/getFlowerBondStatus',
            subscriptionsHaveFetched: 'subscription/subscriptionsHaveFetched',
            hasActiveSubscription: 'subscription/getActiveSubscriptionId',
            activeSubscription: 'subscription/getActiveSubscription',
            multiCoupon: 'gifting/getMultiGiftCoupon',
            singleCoupon: 'gifting/getSingleGiftCoupon',
            hasAnActiveSubscription: 'subscription/hasActiveSubscription',
            specialDatesCoupon: 'gifting/getSpecialDatesGiftCoupon',
            hasUserFetched: 'account/getUserId',
            hasError: 'account/hasError',
            newDashboard: 'account/getDashboardV2Enabled',
            isDesktop: 'account/isDesktop',
            isMobile: 'account/isMobile',
            nextPossibleDate: 'account/getNextPossibleDate',
            bannerData: 'account/getPromotionalBanners',
            isFreeVase: 'account/isFreeVase',
        }),
        subscription() {
            return this.$store.getters['subscription/getSubscriptionById'](this.hasActiveSubscription);
        },
        hasPausedSubscription() {
            return this.subscription?.status === SUBSCRIPTION_STATUS_PAUSED;
        },
        menuItems() {
            const enabled = NavigationLinks.items.filter((item) => { return typeof item.dashboard !== 'undefined' && item.dashboard.enabled; });
            const dashboardItems = enabled.map((item) => Object.assign(item.dashboard, item));
            const sortedItems = dashboardItems.sort((prevItem, nextItem) => prevItem.order - nextItem.order);
            return sortedItems;
        },
        menuItems1() {
            return this.isMobile ? this.menuItems.filter(item => item.newDashboardMobileSection === 1) : this.menuItems.filter(item => item.newDashboardSection === 1);
        },
        menuItems2() {
            return this.isMobile ? this.menuItems.filter(item => item.newDashboardMobileSection === 2) : this.menuItems.filter(item => item.newDashboardSection === 2);
        },
        oldDashboardMenuItems1() {
            return this.isMobile ? this.menuItems.filter(item => item.oldDashboardMobileSection === 1) : this.menuItems.filter(item => item.oldDashboardSection === 1);
        },
        oldDashboardMenuItems2() {
            return this.isMobile ? this.menuItems.filter(item => item.oldDashboardMobileSection === 2) : this.menuItems.filter(item => item.oldDashboardSection === 2);
        },
        randomFact() {
            const randomNumber = Math.floor(Math.random() * FactPool.facts.length);
            return FactPool.facts[randomNumber];
        },
        hasGiftingCoupon() {
            const hasMultiGiftCoupon = typeof this.multiCoupon?.code !== 'undefined';
            const hasSingleGiftCoupon = typeof this.singleCoupon?.code !== 'undefined';
            const hasSpecialDatesCoupon = typeof this.specialDatesCoupon?.code !== 'undefined';
            return (hasMultiGiftCoupon || hasSingleGiftCoupon || hasSpecialDatesCoupon);
        },
        showFunFact() {
            return !this.isFlowerBondHolder && !this.showBanner;
        },
        introductionText() {
            return (this.isFlowerBondHolder || !this.showBanner) ? `${this.firstName}.` : `${this.firstName}`;
        },
        showBanner() {
            return process.env.VUE_APP_BANNER_DASHBOARD === 'true';
        },
        isError() {
            return !this.hasUserFetched && this.hasError;
        },
        canRestartAccount() {
            return this.hasUserFetched && this.subscriptionsHaveFetched && !this.hasAnActiveSubscription && !this.hasPausedSubscription;
        },
        enableTiles() {
            return this.hasUserFetched && this.subscriptionsHaveFetched;
        },
        customStyle() {
            return this.newDashboard ? {} : {
                'margin-bottom': '10px',
                'margin-top': '10px',
            };
        },
    },
    methods: {
        goToShop() {
            this.$router.push({ name: 'account.shop' });
        },
    },
};
</script>

<style lang="scss" scoped>
    .main-container {
        padding-top: 15px;
    }

    .message-banner {
        margin: 0px auto 32px;
        padding: 12px 12px;
        border: 2px double #5AB5D9;
        width: 80%;
        &--content {
            width: 100%;
            padding: 16px;
            margin: 0 auto;
            text-align: center;
            justify-content: center;
            font-style: italic;
        }
    }

    .dashboard-container {
        max-width: 824px;
        margin: 0px auto;
    }

    .recommendation-section {
        margin-top: 48px;
        margin-bottom: 16px;
    }

    @media (max-width: 1024px) {
        .dashboard-container {
            padding-left: 16px;
            padding-right: 16px;
        }
    }

    @media (max-width: 768px) {
        .tiles-section-1 {
            margin-bottom: 48px;
        }
    }
</style>
